"use client";

import { CedeIcon } from "@/components/icons";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { ONBOARDING_STEP_TITLES, WALLET_TYPES } from "@/constants/onboarding";
import { useCreateAccountStep } from "@/hooks/onboarding";
import { Wallet, LogOut, Users, ChevronDown, ChevronUp } from "lucide-react";

export const CreateAccountStep = () => {
  const {
    // State
    isConnecting,
    showReferralInput,
    referralCode,
    setReferralCode,
    isWalletConnected,

    // Ref
    referralInputRef,

    // Computed values
    hasReferrer,
    hubUser,

    // Actions
    handleWalletConnection,
    handleDisconnect,
    handleReferralCodeSave,
    toggleReferralInput,
  } = useCreateAccountStep();

  return (
    <div className="flex flex-col items-center w-full h-full gap-4">
      {/* Fixed Header - Logo and Title */}
      <div className="flex flex-col items-center gap-4 w-full">
        {/* Cede Logo */}
        <div className="w-[120px] h-[120px] flex items-center justify-center flex-shrink-0">
          <CedeIcon width={120} height={120} />
        </div>

        {/* Welcome text */}
        <h1 className="text-[28px] font-bold leading-[140%] text-cede-text-primary text-center w-[313px]">
          {isWalletConnected
            ? ONBOARDING_STEP_TITLES.CREATE_ACCOUNT.CONNECTED
            : ONBOARDING_STEP_TITLES.CREATE_ACCOUNT.NOT_CONNECTED}
        </h1>
      </div>

      {/* Scrollable Content */}
      <div className="flex-1 w-full overflow-y-auto" style={{ minHeight: 0 }}>
        {isWalletConnected ? (
          /* Connected state - Show wallet info and disconnect option */
          <div className="w-full flex flex-col gap-4 items-center">
            {/* Wallet info card with proper theme */}
            {hubUser && (
              <div className="w-full">
                <div className="flex items-center gap-4 bg-cede-bg-component border border-cede-border-container rounded-lg p-4">
                  {/* Avatar */}
                  <div className="w-12 h-12 rounded-full bg-gradient-to-br from-cede-primary to-cede-primary-dark flex items-center justify-center flex-shrink-0">
                    <Wallet size={20} className="text-cede-text-primary" />
                  </div>

                  {/* Address info */}
                  <div className="flex flex-col gap-1 flex-1">
                    <p className="text-cede-text-secondary text-sm">Connected Address</p>
                    <p className="text-cede-text-primary font-mono text-base font-medium">
                      {hubUser.defiAddress
                        ? `${hubUser.defiAddress.slice(0, 6)}...${hubUser.defiAddress.slice(-4)}`
                        : "Connected"}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Action buttons */}
            <div className="w-full">
              {/* Disconnect button - secondary action */}
              <Button
                onClick={handleDisconnect}
                disabled={isConnecting}
                variant="secondary"
                size="large"
                className="w-full h-[56px] justify-center"
                leftIcon={<LogOut size={20} />}
              >
                {isConnecting ? "Disconnecting..." : "Disconnect Wallet"}
              </Button>
            </div>
          </div>
        ) : (
          /* Not connected state - Show wallet connection options */
          <div className="w-full mx-auto flex flex-col gap-4 px-0 md:px-8">
            {/* Wallet connection buttons */}
            <div className="w-full flex flex-col gap-3">
              <Button
                onClick={() => handleWalletConnection(WALLET_TYPES.EVM)}
                disabled={isConnecting}
                variant="primary"
                size="large"
                className="w-full h-[60px] justify-center text-[18px] font-normal leading-[140%] tracking-[0.02em]"
              >
                Connect EVM Wallet
              </Button>

              <Button
                onClick={() => handleWalletConnection(WALLET_TYPES.TON)}
                disabled={isConnecting}
                variant="secondary"
                size="large"
                className="w-full h-[60px] justify-center text-[18px] font-normal leading-[140%] tracking-[0.02em]"
              >
                Connect TON Wallet
              </Button>
            </div>

            {/* Referral Code Section */}
            {!hasReferrer && (
              <div className="w-full">
                {/* Toggle Button */}
                <button
                  onClick={toggleReferralInput}
                  className="w-full flex items-center justify-center gap-2 py-3 text-cede-text-secondary hover:text-cede-text-primary transition-colors"
                >
                  <Users size={16} />
                  <span className="text-sm font-medium">
                    {referralCode ? `Referral Code Applied: ${referralCode}` : "Have a referral code?"}
                  </span>
                  {showReferralInput ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
                </button>

                {/* Referral Input - Collapsible */}
                {showReferralInput && (
                  <div ref={referralInputRef} className="flex flex-col gap-3">
                    <div className="w-full relative">
                      <Input
                        type="text"
                        placeholder="Enter referral code"
                        value={referralCode}
                        onChange={(e) => setReferralCode(e.target.value)}
                        className="h-[48px] pl-4 pr-[120px] py-3 rounded-lg text-base"
                        autoComplete="off"
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            handleReferralCodeSave();
                          }
                        }}
                      />
                      <Button
                        onClick={handleReferralCodeSave}
                        disabled={!referralCode.trim()}
                        variant="primary"
                        size="small"
                        className="absolute right-2 top-2 h-[32px] px-4 text-sm"
                      >
                        Save
                      </Button>
                    </div>

                    {referralCode && (
                      <div className="text-center">
                        <p className="text-xs text-cede-text-grey">
                          Code will be automatically applied when you connect your wallet
                        </p>
                      </div>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};
